import { HTMLAttributes, Ref, forwardRef, useMemo } from "react";
import {
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material";

export type Option = {
  value: string | number;
  label: string;
  props?: any;
};

type Props<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = Omit<
  MuiAutocompleteProps<Option, Multiple, DisableClearable, FreeSolo>,
  'renderInput' | 'value' | 'onChange'
> & {
  TextFieldProps?: Partial<MuiTextFieldProps> | ((params:AutocompleteRenderInputParams) => Partial<MuiTextFieldProps>);
  value?: string | number | (string | number)[] | null;
  onChange: (value: string | number | (string | number)[] | null, reason?:AutocompleteChangeReason) => void;
};

const Autocomplete = forwardRef(<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  // Props
    TextFieldProps,
    // Default option rendering
    renderOption = (props:HTMLAttributes<HTMLLIElement>, option:Option) => (
      <li {...props} key={option.value}>
        {option.label}
      </li>
    ),
    getOptionLabel = (option) => (typeof option === 'object' ? option.label : ''),
    options,
    value,
    onChange,
    isOptionEqualToValue = (option, value) => {
      if ( !option || !value ) return false
      const optionValue = typeof option === 'object' ? option.value : option
      const valueValue = typeof value === 'object' ? value.value : value
      return optionValue === valueValue;
    },
    ...autocompleteProps
  }: Props<Multiple, DisableClearable, FreeSolo>,
  ref: Ref<HTMLDivElement>
) => {
  // Ensure the value is correctly typed, and find the selected option
  const selectedValue = useMemo(() => {
    if ( autocompleteProps.multiple ) {
      return (value as (string | number)[]).map(val => {
        const matchedOption = options.find(opt => opt.value === val);
        return matchedOption ?? val;
      });
    }
    return options.find(option => option.value === value) || null;
  }, [autocompleteProps.multiple, options, value]);

  const handleChange = (_:any, newValue:Option | Option[] | null, reason?:AutocompleteChangeReason) => {
    if ( !onChange ) return;

    if ( autocompleteProps.multiple ) {
      // If multiple is true, return an array of values
      // ToDO: This is a hack to make the autocomplete work with the new design
      onChange(newValue ? (newValue as Option[]).map(option => typeof option === 'object' ? option.value : option) : [], reason);
    } else {
      // If single selection, return a single value
      onChange(newValue ? (newValue as Option).value : null, reason);
    }
  }

  return (
    <MuiAutocomplete
      {...autocompleteProps}
      ref={ref}
      value={selectedValue as any}
      onChange={handleChange as any}
      options={options}
      renderOption={renderOption}
      renderInput={(params) => {
        const textFieldProps = typeof TextFieldProps === 'function'
          ? TextFieldProps(params)
          : TextFieldProps
        ;
        return (
          <MuiTextField
            {...params}
            {...textFieldProps}
            fullWidth={typeof textFieldProps?.fullWidth === 'undefined' ? true : textFieldProps?.fullWidth}
            margin={textFieldProps?.margin || 'normal'}
          />
        );
      }}
      fullWidth={typeof autocompleteProps.fullWidth === 'undefined' ? true : autocompleteProps.fullWidth}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
});

export default Autocomplete;
